import { COMMAND_TYPE, REPLY_TYPE, EVENT_TYPE, MESSAGE_TYPE, getNextCommandId } from './ws.types';

const COMMAND_TYPE_NAMES = {
  [COMMAND_TYPE.PING]: 'TYPE_PING',
  [COMMAND_TYPE.HISTORY_REQUEST]: 'TYPE_HISTORY_REQUEST',
  [COMMAND_TYPE.MESSAGE]: 'TYPE_MESSAGE',
  [COMMAND_TYPE.MESSAGE_READ]: 'TYPE_MESSAGE_READ',
  [COMMAND_TYPE.SLOT_OFFER]: 'TYPE_SLOT_OFFER',
  [COMMAND_TYPE.PACKAGE_OFFER]: 'TYPE_PACKAGE_OFFER',
};

const REPLY_TYPE_NAMES = {
  [REPLY_TYPE.PONG]: 'TYPE_PONG',
  [REPLY_TYPE.HISTORY]: 'TYPE_HISTORY',
};

const EVENT_TYPE_NAMES = {
  [EVENT_TYPE.HELLO]: 'TYPE_HELLO',
  [EVENT_TYPE.MESSAGE]: 'TYPE_MESSAGE',
  [EVENT_TYPE.MESSAGE_READ]: 'TYPE_MESSAGE_READ',
  [EVENT_TYPE.USER_STATUS_CHANGE]: 'TYPE_USER_STATUS_CHANGE',
};

const normalizeType = (type, typeNames) => {
  if (typeof type === 'number') return type;
  if (typeof type === 'string') {
    const found = Object.entries(typeNames).find(([, name]) => name === type);
    return found ? Number(found[0]) : null;
  }

  return null;
};

// payload и card приходят сериализованными JSON-строками — парсим безопасно
const parseJsonSafe = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'object') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const normalizeMessage = (message) => ({
  ...message,
  timestamp: message.timestamp ? Number(message.timestamp) : null,
  is_read: Boolean(message.is_read),
  type: message.type || MESSAGE_TYPE.TEXT,
  payload: parseJsonSafe(message.payload),
  card: parseJsonSafe(message.card),
});

export const wsBuilder = {
  historyRequest: (chatId, page) => ({
    id: getNextCommandId(),
    type: COMMAND_TYPE.HISTORY_REQUEST,
    history_request: {
      chat_id: chatId,
      page,
    },
  }),

  message: (chatId, text, attachment) => ({
    id: getNextCommandId(),
    type: COMMAND_TYPE.MESSAGE,
    message: {
      chat_id: chatId,
      data: text,
      attachments: attachment
        ? [{ file: attachment.file, id: attachment.id }]
        : null,
    },
  }),

  read: (messageId, chatId) => ({
    id: getNextCommandId(),
    type: COMMAND_TYPE.MESSAGE_READ,
    message_read: {
      message_id: messageId,
      chat_id: chatId,
    },
  }),

  // Предложение слота (дата/время/инструмент) — отправляет преподаватель
  slotOffer: (chatId, { date, time, instrument_id }) => ({
    id: getNextCommandId(),
    type: COMMAND_TYPE.SLOT_OFFER,
    slot_offer: {
      chat_id: chatId,
      date,
      time,
      instrument_id,
    },
  }),

  // Предложение пакета занятий — отправляет преподаватель
  packageOffer: (chatId, offerId) => ({
    id: getNextCommandId(),
    type: COMMAND_TYPE.PACKAGE_OFFER,
    package_offer: {
      chat_id: chatId,
      offer_id: offerId,
    },
  }),

  ping: () => ({
    id: getNextCommandId(),
    type: COMMAND_TYPE.PING,
    ping: {},
  }),
};

const safeJsonParse = (raw) => {
  if (typeof MessageEvent !== 'undefined' && raw instanceof MessageEvent) {
    return safeJsonParse(raw.data);
  }

  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

export const wsParser = {
  parseReply: (raw) => {
    const data = safeJsonParse(raw);
    if (!data || typeof data !== 'object') return null;

    // Reply всегда имеет id (ответ на Command)
    if (!('id' in data)) return null;

    let type = normalizeType(data.type, REPLY_TYPE_NAMES);
    if (type === null) {
      if (data.pong) type = REPLY_TYPE.PONG;
      else if (data.history) type = REPLY_TYPE.HISTORY;
    }

    return {
      id: data.id,
      type,
      error: data.error || null,
      pong: data.pong || null,
      warnings: Array.isArray(data.warnings) ? data.warnings : [],
      success: typeof data.success === 'boolean' ? data.success : null,
      history: data.history
        ? {
            ...data.history,
            messages: (data.history.messages || []).map((m) => ({
              ...normalizeMessage(m),
            })),
          }
        : null,
      timestamp: data.timestamp ? Number(data.timestamp) : null,
    };
  },

  parseEvent: (raw) => {
    const data = safeJsonParse(raw);
    if (!data || typeof data !== 'object') return null;

    // Event не имеет id
    if ('id' in data) return null;

    let type = normalizeType(data.type, EVENT_TYPE_NAMES);
    if (type === null) {
      if (data.hello) type = EVENT_TYPE.HELLO;
      else if (data.message) type = EVENT_TYPE.MESSAGE;
      else if (data.message_read) type = EVENT_TYPE.MESSAGE_READ;
      else if (data.user_status_change) type = EVENT_TYPE.USER_STATUS_CHANGE;
    }

    if (type === null) return null;

    return {
      type,
      hello: data.hello
        ? {
            ...data.hello,
            chats: (data.hello.chats || []).map((chat) => ({
              ...chat,
              last_message: chat.last_message
                ? normalizeMessage(chat.last_message)
                : null,
            })),
          }
        : null,
      message: data.message
        ? normalizeMessage(data.message)
        : null,
      message_read: data.message_read || null,
      user_status_change: data.user_status_change || null,
      timestamp: data.timestamp ? Number(data.timestamp) : null,
    };
  },
  COMMAND_TYPE_NAMES,
  REPLY_TYPE_NAMES,
  EVENT_TYPE_NAMES,
};
